// The events feed (docs/ARCHITECTURE.md §2.3): every event fetched so far by id, the types with their emojis, and
// where the data came from. Written only by src/data/load.ts; `events` is a new array whenever the set changes, so
// the filtered set (filtered.ts) can tell by identity.
import { createStore, useStore } from "zustand";
import type { FeedEvent, Tag } from "../data/event";
import { SHARED } from "../shared/config";
import { RULES } from "../shared/rules";
import { profileEvent } from "./profile";

export type Source = "loading" | "server" | "cached" | "error";

export interface FeedState {
  byId: ReadonlyMap<string, FeedEvent>;
  events: readonly FeedEvent[];
  tags: readonly Tag[];
  /** tag (lower case) → emoji, from GET /tags. */
  tagEmoji: ReadonlyMap<string, string>;
  source: Source;
  cachedAt: Date | null;
  /** Events opened by link (/e/<id>) that the feed doesn't hold (outside the fetched area or window). */
  linked: ReadonlyMap<string, FeedEvent>;
}

export const feedStore = createStore<FeedState>()(() => ({
  byId: new Map(), events: [], tags: [], tagEmoji: new Map(), source: "loading", cachedAt: null, linked: new Map(),
}));
export function useFeed<T>(pick: (s: FeedState) => T) { return useStore(feedStore, pick); }

/** A type's emoji from the server (GET /tags), or null. */
export function tagEmoji(tag: string): string | null {
  const e = feedStore.getState().tagEmoji.get(tag.toLowerCase());
  return e && e !== SHARED.fallbackEmoji ? e : null;
}
/** The emoji an event shows everywhere: its primary type's (RULES.primaryEmoji, shared/cases/pin-emoji.json). */
export const emojiOf = (e: FeedEvent) => RULES.primaryEmoji(e, tagEmoji);

/** By id: the feed's copy, else the profile's (starred / hidden events outside the fetched area or window). */
export const eventById = (id: string): FeedEvent | null =>
  feedStore.getState().byId.get(id) ?? feedStore.getState().linked.get(id) ?? (profileEvent(id) as FeedEvent | null);

// Per event, worked out once (docs/MAP.md "Data pipeline"): its tags normalized (RULES.norm).
const tagKeyCache = new WeakMap<FeedEvent, string[]>();
export function tagKeys(e: FeedEvent) {
  let k = tagKeyCache.get(e);
  if (!k) { k = e.tags.map(RULES.norm); tagKeyCache.set(e, k); }
  return k;
}
/** RULES.interestLevel with the event's tags normalized once: "interest" when any is one of yours. */
export const interestLevelOf = (e: FeedEvent, interests: ReadonlySet<string>) =>
  (tagKeys(e).some((t) => interests.has(t)) ? "interest" : "none") as "interest" | "none";
